import React, { useState } from 'react';

/**
 * PasswordInput — wraps a password <input> with a "Show / Hide" toggle.
 *
 * Drop-in replacement for any `<input type="password" ... />` — passes
 * all standard input props through (value, onChange, required, minLength,
 * autoComplete, placeholder, …).  When the toggle is pressed the input
 * `type` flips between "password" and "text"; nothing is logged, leaked,
 * or persisted.
 *
 * Used by the login, register, password-change, and password-reset
 * forms so the UX is consistent everywhere a password is entered.
 *
 * The button is rendered absolutely over the right edge of the input,
 * so callers don't need to allocate extra layout space.  The wrapper
 * div uses `display: block` so the input still fills the form-group's
 * width.
 */
export default function PasswordInput({
  value,
  onChange,
  required,
  minLength,
  autoComplete = 'current-password',
  placeholder,
  // Allow the caller to pass an id / name for accessibility &
  // browser password-manager integration.
  id,
  name,
  ...rest
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div
      style={{
        position: 'relative',
        display: 'block',
      }}
    >
      <input
        {...rest}
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        placeholder={placeholder}
        // Leave room for the toggle button so long passwords don't run
        // underneath it.  56px ≈ "Hide" + padding.
        style={{
          width: '100%',
          paddingRight: 56,
          boxSizing: 'border-box',
          ...(rest.style || {}),
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        // Subtle, accessible toggle — sits over the right edge of the
        // input. Uses transparent background so it inherits whatever
        // theming the surrounding card applies.
        style={{
          position: 'absolute',
          top: '50%',
          right: 8,
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: '#475569',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.3,
          padding: '4px 8px',
          cursor: 'pointer',
          borderRadius: 4,
        }}
        // Tabbing through the form lands on the input → toggle → next
        // field, which matches user expectations.  No tabindex override.
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}
